'use client';

import {
  autoPlacement,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloatingToolbar,
  useFloatingToolbarState,
} from '@platejs/floating';
import { useEditorId, useEventEditorValue } from 'platejs/react';

import {
  FieldBulletedListToolbarButton,
  FieldNumberedListToolbarButton,
} from '@/components/ui/field-list-toolbar-button';
import { MarkToolbarButton } from '@/components/ui/mark-toolbar-button';
import { FieldTurnIntoToolbarButton } from '@/components/ui/field-turn-into-toolbar-button';
import { Toolbar, ToolbarGroup } from '@/components/ui/toolbar';
import { cn } from '@/lib/utils';
import { BoldIcon, ItalicIcon } from 'lucide-react';

/** Bold / italic / turn-into / lists — shared by fixed and floating field toolbars. */
export function FieldFormatToolbar({ formatDisabled }: { formatDisabled?: boolean }) {
  const disableProps = formatDisabled ? { disabled: true as const } : {};

  return (
    <>
      <ToolbarGroup>
        <FieldTurnIntoToolbarButton {...disableProps} />
      </ToolbarGroup>
      <ToolbarGroup>
        <MarkToolbarButton nodeType="bold" tooltip="Bold" {...disableProps}>
          <BoldIcon />
        </MarkToolbarButton>
        <MarkToolbarButton nodeType="italic" tooltip="Italic" {...disableProps}>
          <ItalicIcon />
        </MarkToolbarButton>
      </ToolbarGroup>
      <ToolbarGroup>
        <FieldBulletedListToolbarButton {...disableProps} />
        <FieldNumberedListToolbarButton {...disableProps} />
      </ToolbarGroup>
    </>
  );
}

/** Contextual formatting toolbar on text selection in field/document Plate editors. */
export function FieldFloatingToolbar() {
  const editorId = useEditorId();
  const focusedEditorId = useEventEditorValue('focus');

  const state = useFloatingToolbarState({
    editorId,
    focusedEditorId,
    floatingOptions: {
      strategy: 'fixed',
      placement: 'top-start',
      middleware: [
        offset(12),
        shift({ padding: 12 }),
        flip({ padding: 12 }),
        autoPlacement({ allowedPlacements: ['top-start', 'top', 'top-end'] }),
      ],
    },
  });

  const { clickOutsideRef, hidden, props, ref } = useFloatingToolbar(state);

  if (hidden) return null;

  return (
    <FloatingPortal>
      <div ref={clickOutsideRef} className="ignore-click-outside/toolbar">
        <div ref={ref} style={props.style} className="z-[100]">
          <Toolbar
            className={cn(
              'gap-0.5 rounded-md border border-border bg-popover px-1 py-0.5 shadow-md',
            )}
          >
            <FieldFormatToolbar />
          </Toolbar>
        </div>
      </div>
    </FloatingPortal>
  );
}
